import React from 'react';
import { Icon } from '@/components/ui/Icon';
import { Colors } from '@/constants/Colors';
import { useTheme } from '@/hooks/useTheme';
import { getObservationCategoryBadge } from '@/utils/competitionLabel';
import {
  formatObservationPlayerLabel,
  formatObservationPosteBadge,
  getObservationPlayerInitials,
  hasObservationPlayerName,
} from '@/utils/observationDisplay';
import type { Observation } from '@/types';

/** Étoiles / note dans les listes d’observations */
const RATING_STAR_COLOR = '#fdd663';
interface ObservationCardProps {
  observation: Observation;
  /** Libellé catégorie forcé (ex. depuis la fiche match parente). */
  categoryLabel?: string;
  showMatchInfo?: boolean;
  onPress?: () => void;
}

function formatDate(value?: string): string {
  if (!value) return '-';
  const d = new Date(value);
  if (isNaN(d.getTime())) return value;
  return d.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' });
}

export function ObservationCard({
  observation,
  categoryLabel,
  showMatchInfo = true,
  onPress,
}: ObservationCardProps) {
  const colors = useTheme();
  const initials = getObservationPlayerInitials(observation);
  const showPlayerNumber = Boolean(observation.playerNumber) && !hasObservationPlayerName(observation);
  const criteriaAverage = Number.isFinite(observation.rating) ? observation.rating : 0;
  const tifosiScore = Number.isFinite(observation.robustRating) ? Number(observation.robustRating) : criteriaAverage;
  const criteriaDisplay = criteriaAverage ? criteriaAverage.toFixed(1) : '-';
  const tifosiDisplay = tifosiScore ? tifosiScore.toFixed(1) : '-';
  const playerLabel = formatObservationPlayerLabel(observation);
  const observationDate = formatDate(observation.date);
  const creatorLabel = (observation.author || observation.tifosi || '').trim() || 'tifosi';
  const categoryBadge = categoryLabel?.trim() || getObservationCategoryBadge(observation);
  const posteBadge = formatObservationPosteBadge(observation.poste);

  return (
    <div
      onClick={onPress}
      style={{
        position: 'relative',
        borderRadius: 16,
        padding: 14,
        border: `1px solid ${colors.border}`,
        backgroundColor: colors.surface,
        marginBottom: 10,
        cursor: onPress ? 'pointer' : 'default',
      }}
    >
      <div
        style={{
          position: 'absolute',
          top: 10,
          right: 10,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-end',
          gap: 6,
          flexShrink: 0,
          zIndex: 1,
        }}
        aria-hidden
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            backgroundColor: 'rgba(253, 214, 99, 0.18)',
            padding: '4px 8px',
            borderRadius: 12,
          }}
        >
          <Icon name="star" size={12} color={RATING_STAR_COLOR} />
          <span style={{ color: RATING_STAR_COLOR, fontSize: 12, fontWeight: 700 }}>{criteriaDisplay}</span>
        </div>
        <div
          style={{
            fontSize: 10,
            fontWeight: 700,
            borderRadius: 999,
            padding: '3px 10px',
            border: `1px solid ${Colors.primary}50`,
            backgroundColor: `${Colors.primary}20`,
            color: Colors.primary,
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            lineHeight: 1,
          }}
        >
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 20,
              height: 20,
              borderRadius: 999,
              backgroundColor: '#d9262e',
              color: '#ffffff',
              fontSize: 12,
              fontWeight: 700,
              boxShadow: '0 4px 10px rgba(217, 38, 46, 0.35)',
            }}
            aria-hidden
          >
            T
          </span>
          <span
            style={{
              color: Colors.primary,
              fontSize: 12,
              fontWeight: 700,
              letterSpacing: 0.1,
            }}
          >
            {tifosiDisplay}
          </span>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 12 }}>
        <div style={{ position: 'relative', flexShrink: 0 }}>
          <div
            style={{
              width: 56,
              height: 56,
              borderRadius: 28,
              backgroundColor: `${Colors.primary}15`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: `2px solid ${Colors.primary}30`,
            }}
          >
            <span style={{ fontSize: 16, fontWeight: 700, color: Colors.primary }}>{initials || '?'}</span>
          </div>
          {showPlayerNumber && (
            <div
              style={{
                position: 'absolute',
                bottom: -4,
                right: -4,
                backgroundColor: Colors.primary,
                padding: '2px 6px',
                borderRadius: 10,
                border: `2px solid ${colors.surface}`,
              }}
            >
              <span style={{ color: '#ffffff', fontSize: 10, fontWeight: 700 }}>
                {observation.playerNumber}
              </span>
            </div>
          )}
        </div>

        <div style={{ flex: 1, minWidth: 0, paddingRight: 52 }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <span
              style={{
                fontSize: 15,
                fontWeight: 700,
                color: colors.text,
                whiteSpace: 'normal',
                wordBreak: 'break-word',
                lineHeight: 1.25,
              }}
            >
              {playerLabel}
            </span>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              {observation.isPrivate ? (
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 4,
                    backgroundColor: `${colors.textSecondary}14`,
                    border: `1px solid ${colors.border}`,
                    padding: '4px 10px',
                    borderRadius: 12,
                    flexShrink: 0,
                  }}
                >
                  <Icon name="lock-closed-outline" size={12} color={colors.textSecondary} />
                  <span style={{ fontSize: 11, fontWeight: 700, color: colors.textSecondary, letterSpacing: 0.2 }}>
                    Privé
                  </span>
                </div>
              ) : null}
              {posteBadge ? (
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 4,
                    backgroundColor: `${Colors.primary}12`,
                    border: `1px solid ${Colors.primary}40`,
                    padding: '4px 10px',
                    borderRadius: 12,
                    flexShrink: 0,
                  }}
                >
                  <Icon name="shield-outline" size={12} color={Colors.primary} />
                  <span style={{ fontSize: 11, fontWeight: 700, color: Colors.primary, letterSpacing: 0.2 }}>
                    {posteBadge}
                  </span>
                </div>
              ) : null}
              {categoryBadge ? (
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 4,
                    backgroundColor: `${Colors.primary}12`,
                    border: `1px solid ${Colors.primary}40`,
                    padding: '4px 10px',
                    borderRadius: 12,
                    flexShrink: 0,
                  }}
                >
                  <Icon name="football-outline" size={12} color={Colors.primary} />
                  <span style={{ fontSize: 11, fontWeight: 700, color: Colors.primary, letterSpacing: 0.2 }}>
                    {categoryBadge}
                  </span>
                </div>
              ) : null}
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 4,
                  backgroundColor: `${colors.textSecondary}14`,
                  border: `1px solid ${colors.border}`,
                  padding: '4px 8px',
                  borderRadius: 12,
                  flexShrink: 0,
                }}
              >
                <Icon name="calendar-outline" size={12} color={colors.textSecondary} />
                <span style={{ fontSize: 11, fontWeight: 600, color: colors.textSecondary }}>{observationDate}</span>
              </div>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 4,
                  backgroundColor: `${colors.textSecondary}14`,
                  border: `1px solid ${colors.border}`,
                  padding: '4px 8px',
                  borderRadius: 12,
                  flexShrink: 0,
                }}
              >
                <Icon name="person-outline" size={12} color={colors.textSecondary} />
                <span style={{ fontSize: 11, fontWeight: 600, color: colors.textSecondary }}>{creatorLabel}</span>
              </div>
            </div>
          </div>

          {showMatchInfo && observation.matchInfo && (
            <div
              style={{
                fontSize: 12,
                marginTop: 4,
                color: colors.textSecondary,
                whiteSpace: 'normal',
                wordBreak: 'break-word',
              }}
            >
              {observation.matchInfo}
            </div>
          )}

          {observation.comments && (
            <div
              style={{
                fontSize: 12,
                marginTop: 6,
                color: colors.text,
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
              }}
            >
              {observation.comments}
            </div>
          )}

        </div>
      </div>
    </div>
  );
}

export default ObservationCard;
